import { useCallback, useEffect, useState } from "react";
import { CalendarPlus, Pencil, Trash2, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { API, api } from "@/lib/api";

const EMPTY = {
  title: "", subtitle: "", start: "", end: "", location_name: "", address: "",
  highlights: "", description: "", rsvp_url: "",
};

const LA_TZ = "America/Los_Angeles";
const laParts = (date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: LA_TZ, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(date);
  return Object.fromEntries(parts.map((p) => [p.type, p.value]));
};
// datetime-local values are always treated as Los Angeles time, whatever the admin's browser timezone
const toLocalInput = (iso) => {
  const p = laParts(new Date(iso));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};
const fromLocalInput = (value) => {
  const [d, t] = value.split("T");
  const [y, m, day] = d.split("-").map(Number);
  const [h, min] = t.split(":").map(Number);
  const guess = Date.UTC(y, m - 1, day, h, min);
  const p = laParts(new Date(guess));
  const asLA = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute);
  return new Date(guess + (guess - asLA)).toISOString();
};
const fmt = (iso) => new Date(iso).toLocaleString("en-US", { timeZone: LA_TZ, month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
const field = "w-full border border-[#E5E1DA] px-4 py-3 outline-none focus:border-[#d4a373] bg-white text-sm";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.15em] text-[#2c5865]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function EventForm({ token, editing, onDone, onUnauthorized }) {
  const [form, setForm] = useState(EMPTY);
  const [image, setImage] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setImage(null);
    setRemoveImage(false);
    setForm(editing ? {
      title: editing.title, subtitle: editing.subtitle, start: toLocalInput(editing.starts_at), end: toLocalInput(editing.ends_at),
      location_name: editing.location_name, address: editing.address, highlights: editing.highlights,
      description: editing.description, rsvp_url: editing.rsvp_url,
    } : EMPTY);
  }, [editing]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (fromLocalInput(form.end) <= fromLocalInput(form.start)) return toast.error("End time must be after the start time");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (k !== "start" && k !== "end") fd.append(k, v); });
    fd.append("starts_at", fromLocalInput(form.start));
    fd.append("ends_at", fromLocalInput(form.end));
    if (image) fd.append("image", image);
    if (editing && removeImage) fd.append("remove_image", "true");
    setSaving(true);
    try {
      const cfg = { headers: { "Content-Type": "multipart/form-data", "X-Admin-Token": token } };
      if (editing) await api.put(`/events/${editing.id}`, fd, cfg);
      else await api.post("/events", fd, cfg);
      toast.success(editing ? "Event updated" : "Event added");
      setForm(EMPTY);
      setImage(null);
      e.target.reset();
      onDone();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      toast.error(err.response?.data?.detail || "Could not save event");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#E5E1DA] p-5 md:p-8 space-y-4 min-w-0" data-testid="event-form">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl text-[#1e4d5d]" style={{ fontFamily: "Playfair Display, serif" }}>
          {editing ? "Edit event" : "Add an event"}
        </h2>
        {editing && (
          <button type="button" onClick={() => onDone()} className="text-sm text-[#2c5865] flex items-center gap-1 hover:text-[#d4a373]" data-testid="event-form-cancel">
            <X size={14} /> Cancel
          </button>
        )}
      </div>
      <Field label="Event name *"><input className={`${field} min-w-0`} value={form.title} onChange={set("title")} required data-testid="event-title-input" /></Field>
      <Field label="Tagline"><input className={`${field} min-w-0`} value={form.subtitle} onChange={set("subtitle")} placeholder="e.g. First-Time Homebuyer Seminar" data-testid="event-subtitle-input" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Starts * (LA time)"><input type="datetime-local" className={`${field} min-w-0`} value={form.start} onChange={set("start")} required data-testid="event-start-input" /></Field>
        <Field label="Ends * (LA time)"><input type="datetime-local" className={`${field} min-w-0`} value={form.end} onChange={set("end")} required data-testid="event-end-input" /></Field>
      </div>
      <Field label="Venue name"><input className={`${field} min-w-0`} value={form.location_name} onChange={set("location_name")} placeholder="e.g. KW L.A. Harbor Office" data-testid="event-venue-input" /></Field>
      <Field label="Address (adds a map link)"><input className={`${field} min-w-0`} value={form.address} onChange={set("address")} placeholder="Street, City, CA ZIP" data-testid="event-address-input" /></Field>
      <Field label="Highlights"><input className={`${field} min-w-0`} value={form.highlights} onChange={set("highlights")} placeholder="Free · Refreshments · Free parking" data-testid="event-highlights-input" /></Field>
      <Field label="Description"><textarea rows={5} className={`${field} min-w-0`} value={form.description} onChange={set("description")} data-testid="event-description-input" /></Field>
      <Field label="RSVP link (Eventbrite, etc.)"><input type="url" className={`${field} min-w-0`} value={form.rsvp_url} onChange={set("rsvp_url")} placeholder="https://" data-testid="event-rsvp-input" /></Field>
      <Field label="Photo or flyer">
        <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setImage(e.target.files[0])} className="block w-full max-w-full text-sm" data-testid="event-image-input" />
      </Field>
      {editing?.has_image && !image && (
        <label className="flex items-center gap-2 text-sm text-[#2c5865]">
          <input type="checkbox" checked={removeImage} onChange={(e) => setRemoveImage(e.target.checked)} data-testid="event-remove-image" />
          Remove current photo
        </label>
      )}
      <button className="btn-primary flex items-center gap-2" disabled={saving} data-testid="event-form-submit">
        <CalendarPlus size={16} /> {saving ? "Saving…" : editing ? "Save changes" : "Add event"}
      </button>
    </form>
  );
}

export function EventsManager({ token, onUnauthorized }) {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get("/events").then((r) => setEvents(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"? This removes it from the Events page.`)) return;
    try {
      await api.delete(`/events/${ev.id}`, { headers: { "X-Admin-Token": token } });
      toast.success("Event deleted");
      if (editing?.id === ev.id) setEditing(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      toast.error("Could not delete event");
    }
  };

  const now = Date.now();
  const sorted = [...events].sort((a, b) => new Date(b.starts_at) - new Date(a.starts_at));

  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[480px_1fr] items-start [&>*]:min-w-0" data-testid="events-manager">
      <EventForm token={token} editing={editing} onUnauthorized={onUnauthorized} onDone={() => { setEditing(null); load(); }} />
      <div className="space-y-4" data-testid="events-admin-list">
        {sorted.length === 0 && <p className="text-[#2c5865]">No events yet. Add your first one.</p>}
        {sorted.map((ev) => {
          const past = new Date(ev.ends_at).getTime() < now;
          return (
            <div key={ev.id} className={`bg-white border p-5 flex gap-5 items-center ${editing?.id === ev.id ? "border-[#d4a373]" : "border-[#E5E1DA]"}`} data-testid={`events-admin-item-${ev.id}`}>
              <div className="w-20 h-20 shrink-0 bg-[#f3ece0] flex items-center justify-center overflow-hidden">
                {ev.has_image
                  ? <img src={`${API}/events/${ev.id}/image?v=${encodeURIComponent(ev.updated_at)}`} alt="" className="w-full h-full object-cover" />
                  : <ImagePlus size={20} className="text-[#d4a373]" />}
              </div>
              <div className="flex-1 min-w-0">
                <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 ${past ? "bg-[#eee] text-[#777]" : "bg-[#e8c79a]/40 text-[#1e4d5d]"}`}>
                  {past ? "Past" : "Upcoming"}
                </span>
                <p className="mt-1.5 text-[#1e4d5d] font-medium truncate">{ev.title}</p>
                <p className="text-xs text-[#2c5865]/80">{fmt(ev.starts_at)}</p>
              </div>
              <button onClick={() => { setEditing(ev); window.scrollTo({ top: 0, behavior: "smooth" }); }} aria-label="Edit" className="p-2 text-[#2c5865] hover:text-[#d4a373]" data-testid={`events-admin-edit-${ev.id}`}>
                <Pencil size={16} />
              </button>
              <button onClick={() => remove(ev)} aria-label="Delete" className="p-2 text-[#2c5865] hover:text-red-700" data-testid={`events-admin-delete-${ev.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
